'use client';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { cn } from '@/src/shared/lib/utils';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * MarkdownRenderer - Renders markdown content with proper styling
 * Used for legal documents (Privacy Policy, Terms of Service, Cookie Policy)
 *
 * @param content - Markdown string to render
 * @param className - Optional additional CSS classes
 */
export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div
      className={cn(
        'prose prose-slate dark:prose-invert max-w-none',
        'prose-headings:font-bold prose-headings:text-text-primary',
        'prose-h1:text-4xl prose-h1:mb-4',
        'prose-h2:text-3xl prose-h2:mt-8 prose-h2:mb-4',
        'prose-h3:text-2xl prose-h3:mt-6 prose-h3:mb-3',
        'prose-p:text-text-secondary prose-p:leading-relaxed prose-p:mb-4',
        'prose-a:text-accent-1 prose-a:no-underline hover:prose-a:underline',
        'prose-strong:text-text-primary prose-strong:font-semibold',
        'prose-ul:list-disc prose-ul:my-4 prose-ul:pl-6',
        'prose-ol:list-decimal prose-ol:my-4 prose-ol:pl-6',
        'prose-li:text-text-secondary prose-li:mb-2',
        'prose-code:text-accent-2 prose-code:bg-surface-secondary prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm',
        'prose-pre:bg-surface-secondary prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto',
        'prose-blockquote:border-l-4 prose-blockquote:border-accent-1 prose-blockquote:pl-4 prose-blockquote:italic',
        'prose-table:border-collapse prose-table:w-full',
        'prose-th:border prose-th:border-border-primary prose-th:p-2 prose-th:bg-surface-secondary',
        'prose-td:border prose-td:border-border-primary prose-td:p-2',
        className
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
